import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Globe,
  Lock,
  Mail,
  MessageSquareText,
  MoreHorizontal,
  Phone,
  QrCode,
  Slack,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND_MASCOT_LARGE } from "@/data/brandMascots";
import { cn } from "@/lib/utils";
import { geniePageUrl, isPageLive } from "@/pages/UserHub/GeniePages/geniePageStatus";
import { Agent } from "@/types/agent/agent";

const TeamsLogo = ({ className }: { className?: string }) => (
  <img
    src="/platform-icons/Microsoft_Office_Teams_(2018–present).svg.png"
    alt=""
    className={cn("object-contain", className)}
  />
);

interface ShareOverviewSectionProps {
  genie: Agent;
  /** Conversation link (carries the app's "save this genie" receive intent). */
  shareUrl: string;
  /** App-first link — lands the genie in the recipient's My Genies. */
  keepUrl: string | null;
  onGoToManageAccess: () => void;
  onGoToPhone: () => void;
  onGoToMailbox: () => void;
  onGoToSlack: () => void;
  onGoToTeams: () => void;
  onGoToActivation: () => void;
  onDownloadQR: () => void;
  onClose: () => void;
}

export default function ShareOverviewSection({
  genie,
  shareUrl,
  keepUrl,
  onGoToManageAccess,
  onGoToPhone,
  onGoToMailbox,
  onGoToSlack,
  onGoToTeams,
  onGoToActivation,
  onDownloadQR,
  onClose,
}: ShareOverviewSectionProps) {
  const isPublic = genie.is_public;
  const hasPhone = !!genie.phone?.number;
  const hasMailbox = genie.mailbox?.status === "provisioned" && !!genie.mailbox.email_address;
  const pageUrl = isPageLive(genie) ? geniePageUrl(genie) : null;

  const copyToClipboard = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleShareLink = () => {
    if (isPublic) {
      copyToClipboard(shareUrl, "Link copied to clipboard");
    } else {
      onGoToManageAccess();
    }
  };

  // Alternate share-link paths, all pointing at the same genie — `/live-agent/`
  // is the default surfaced on the tile itself; these live behind "More".
  const genieUrlNameForLinks = genie.url_name ?? genie.page?.url_name;
  const alternateLinkPaths = [
    { key: "studio", label: "Studio link", path: "studio" },
    { key: "agent", label: "Agent link", path: "agent" },
    { key: "genie", label: "Genie link", path: "genie" },
  ] as const;
  const handleCopyAlternateLink = (path: string) => {
    if (!genieUrlNameForLinks) return;
    const url = `${window.location.origin}/${path}/${genieUrlNameForLinks}`;
    copyToClipboard(url, "Link copied to clipboard");
  };

  // Copies the /keep link. On a phone with the app installed the OS hands it
  // straight to Help Genie, which saves the genie into My Genies and opens the
  // conversation; without the app it lands on the web /keep page instead of
  // dead-ending.
  const handleShareToApp = () => {
    if (!keepUrl) {
      onGoToActivation();
      return;
    }
    copyToClipboard(keepUrl, "App link copied — it saves this genie into their Help Genie app");
  };

  const handleSharePhone = () => {
    if (hasPhone) {
      copyToClipboard(genie.phone!.number!, "Phone number copied to clipboard");
    } else {
      onGoToPhone();
    }
  };

  const handleShareMailbox = () => {
    if (hasMailbox) {
      copyToClipboard(genie.mailbox!.email_address!, "Mailbox address copied to clipboard");
    } else {
      onGoToMailbox();
    }
  };

  const tiles = [
    {
      key: "link",
      icon: MessageSquareText,
      label: "Share Link",
      description: isPublic ? "Copy the public link" : "Create a private access link",
      onClick: handleShareLink,
      disabled: false,
    },
    {
      key: "app",
      icon: Smartphone,
      label: "Send to their app",
      description: keepUrl ? "Saves this genie into their Help Genie app" : "Finish setup to get an app link",
      onClick: handleShareToApp,
      disabled: false,
    },
    {
      key: "qr",
      icon: QrCode,
      label: "Share QR Code",
      description: "Download the QR code",
      onClick: onDownloadQR,
      disabled: false,
    },
    {
      key: "phone",
      icon: Phone,
      label: "Share Phone",
      description: hasPhone ? "Copy the phone number" : "No phone number attached",
      onClick: handleSharePhone,
      disabled: !hasPhone,
    },
    {
      key: "mailbox",
      icon: Mail,
      label: "Share Mailbox",
      description: hasMailbox ? "Copy the mailbox address" : "Set up a mailbox for this genie",
      onClick: handleShareMailbox,
      disabled: false,
    },
    // The Genie Page is the hub every other channel can point at: guides,
    // FAQs and every way in, on one stable URL.
    {
      key: "page",
      icon: BookOpen,
      label: "Share Genie Page",
      description: pageUrl ? "Copy a link to the full help centre" : "Publish the page to share it",
      onClick: () => {
        if (pageUrl) {
          copyToClipboard(pageUrl, "Genie Page link copied to clipboard");
        }
      },
      disabled: !pageUrl,
    },
    {
      key: "slack",
      icon: Slack,
      label: "Share in Slack",
      description: "Let customers chat with it in their Slack",
      onClick: onGoToSlack,
      disabled: false,
    },
    {
      key: "teams",
      icon: TeamsLogo,
      label: "Share in Teams",
      badge: "Experimental",
      description: "Let customers chat with it in their Microsoft Teams",
      onClick: onGoToTeams,
      disabled: false,
    },
  ];

  return (
    <div className="space-y-6 pt-4">
      <div
        className={`rounded-xl border p-4 ${isPublic ? "border-blue-200 bg-blue-50" : "border-amber-200 bg-amber-50"}`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              isPublic ? "bg-blue-100" : "bg-amber-100"
            }`}
          >
            {isPublic ? (
              <Globe className="h-4.5 w-4.5 text-blue-600" />
            ) : (
              <Lock className="h-4.5 w-4.5 text-amber-600" />
            )}
          </div>
          <div>
            <p className={`text-sm font-semibold ${isPublic ? "text-blue-900" : "text-amber-900"}`}>
              {isPublic ? "This genie is public" : "This genie is private"}
            </p>
            <p className={`text-sm mt-1 ${isPublic ? "text-blue-800" : "text-amber-800"}`}>
              {isPublic
                ? "Anyone with the link can access and use this genie."
                : "The raw share link only works for the owner and team members. Anyone else needs an invite or an access link from the Manage Access tab."}
            </p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Share options</h4>
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((tile) => (
            <div key={tile.key} className="relative">
              <button
                type="button"
                onClick={tile.onClick}
                disabled={tile.disabled}
                className="flex w-full flex-col items-start gap-1.5 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
              >
                <div className={`flex flex-wrap items-center gap-2 ${tile.key === "link" ? "pr-6" : ""}`}>
                  <tile.icon className="h-5 w-5 shrink-0 text-gray-500" />
                  <p className="text-sm font-semibold text-gray-900">{tile.label}</p>
                  {tile.badge && (
                    <Badge className="bg-genie-primary/10 px-2 text-[11px] text-genie-primary hover:bg-genie-primary/10">
                      {tile.badge}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">{tile.description}</p>
              </button>
              {tile.key === "link" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="More link options"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {alternateLinkPaths.map((link) => (
                      <DropdownMenuItem
                        key={link.key}
                        disabled={!genieUrlNameForLinks}
                        onClick={() => handleCopyAlternateLink(link.path)}
                      >
                        Copy {link.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </div>

      <Link
        to={`/genie-hub/activation/genie/${genie.id}`}
        onClick={onClose}
        className="group relative flex flex-col sm:flex-row sm:items-center gap-4 p-6 rounded-2xl overflow-hidden border border-transparent text-white transition-all hover:-translate-y-0.5 hover:shadow-xl"
        style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
      >
        <span
          className="pointer-events-none absolute -top-1/3 -right-1/4 w-2/3 h-[170%] rotate-12 bg-white/10 blur-2xl"
          aria-hidden
        />
        <span className="relative w-14 h-14 rounded-2xl bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src={BRAND_MASCOT_LARGE} alt="" className="w-[85%] h-[85%] object-contain" />
        </span>
        <div className="relative flex-1 min-w-0">
          <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-white/80 block">
            Get it out there
          </span>
          <div className="text-xl font-bold leading-tight mt-1">Activation Pack</div>
          <p className="text-sm text-white/85 leading-snug mt-1.5">
            QR codes, embed snippet, email signature, and print-ready assets — all in one place.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {["QR codes", "Shareable link", "Posters", "Email signature"].map((chip) => (
              <span
                key={chip}
                className="px-3 py-1 rounded-full bg-white/15 ring-1 ring-white/20 text-xs font-semibold"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
        <span className="relative self-start sm:self-center w-11 h-11 rounded-full bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 transition-transform group-hover:translate-x-0.5">
          <ArrowRight className="w-5 h-5" />
        </span>
      </Link>
    </div>
  );
}
